import { useEffect, useMemo, useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Text } from '@myrealtrip/web-ui';

import { DEFAULT_TRAVELER_AGE as DEFAULT_FOCUS_AGE } from '~constants/pointToPointTicket';
import { usePrevious } from '~hooks/usePrevious';

interface Props {
  selectedAge: number | null;
  displayAge: number;
  isOpen: boolean;
  isLast: boolean;
  onClick: (newAge: number) => () => void;
}

const TravelerAgeSelectSheetOption = ({
  selectedAge,
  displayAge,
  isOpen,
  isLast,
  onClick,
}: Props) => {
  const ref = useRef<HTMLLIElement>(null);
  const previousIsOpen = usePrevious(isOpen);
  const isSelected = useMemo(() => selectedAge === displayAge, [selectedAge, displayAge]);
  const isDefaultFocusAge = useMemo(() => displayAge === DEFAULT_FOCUS_AGE, [displayAge]);
  const hasSelectedAge = useMemo(() => selectedAge !== null, [selectedAge]);

  useEffect(() => {
    const isFirstRender = previousIsOpen !== isOpen;

    if (!ref || !ref.current || !isFirstRender) {
      return;
    }

    if (hasSelectedAge && isSelected) {
      ref.current.scrollIntoView({ block: 'center' });
    }

    if (!hasSelectedAge && isDefaultFocusAge) {
      ref.current.scrollIntoView({ block: 'center' });
    }
  }, [hasSelectedAge, isSelected, isDefaultFocusAge, previousIsOpen, isOpen]);

  return (
    <Option ref={ref} isSelected={isSelected} onClick={onClick(displayAge)}>
      <Text typography="headlineBold22">
        {isLast ? `만 ${displayAge}세 이상` : `만 ${displayAge}세`}
      </Text>
    </Option>
  );
};

export default TravelerAgeSelectSheetOption;

const Option = styled.li<{ isSelected: boolean }>`
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme: { colors } }) => colors.gray.s1000};
  opacity: 0.1;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.colors.gray.s60};
      opacity: 1;
      padding: 8px 0;
    `}
`;
